import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from '../../i18n/locale-context';
import { keyboardVisibleBand, useVisualViewport } from '../../lib/viewport';
import { useCoarsePointer } from '../../lib/device-context';
import { countryConfig } from '../../country';
import { noIkpuCodeFound } from '../../lib/ikpu-search-fallback';
import {
  fetchCategories,
  searchIkpu,
  fetchIkpuCodes,
  searchNiches,
  type CatalogFilters,
  type IkpuItem,
} from '../../lib/catalog-client';

/**
 * Extracted from the catalog's `FilterBar` (2026-08) so a rich, searchable
 * subject picker isn't a catalog-only luxury — a second consumer (the
 * saved-search builder) shipped with a bare `Select`.
 *
 * One of category / ИКПУ / niche — the SAME single subject axis the catalog
 * filter panel treats as one choice: broad → curated → precise.
 */
export type SubjectSelection =
  | { kind: 'category'; slug: string; name: string }
  | { kind: 'ikpu'; code: string; name: string }
  | { kind: 'niche'; slug: string; name: string };

/**
 * Kazakhstan: the picker browses a 9 900-code classifier (ЕНС ТРУ) whose names
 * are item-level («Бензин для двигателей…», «Бумага для офисного оборудования…»),
 * not a supplier's trade. Measured 2026-10-15…16 on kaztender.com: 0 of 204 Meta
 * visitors picked anything from it (uztender.com: 5 %). So there the curated
 * niches — human names — lead the browse list (`nichesFirst`) and the codes are
 * revealed twelve at a time instead of six. Uzbekistan is untouched.
 */
// Kyrgyzstan's ОКГЗ is an item-level classifier too (13 412 nodes): KZ's order.
const IS_KZ = countryConfig.code !== 'uz';

/** Browse-mode page sizes: how many rows of each taxonomy to reveal per "показать ещё". */
const CAT_PAGE = 8;
const IKPU_PAGE = IS_KZ ? 12 : 6;
const NICHE_PAGE = 8;

/**
 * Left inset per nesting level for an ИКПУ row, index = depth. See the
 * catalog's original comment (now here) for why this exists: ИКПУ counts are
 * hierarchical — `49.41` already contains `49.41.19.900`, which already
 * contains `49.41.19.90000002` — so a flat, identically-styled list reads as
 * broken arithmetic. Static strings, not a computed class, because Tailwind
 * only ships classes it can see. Capped at 3.
 */
/**
 * ИКПУ rows are NOT indented by depth.
 *
 * The nesting is already written in the code itself — `71` → `71.12` →
 * `71.12.12` — so every code starts at ONE left edge and the shared prefix
 * lines up as a column you can read straight down. Indenting on top of that
 * shifted each level right and destroyed exactly that alignment.
 */
/*
 * Row size on a phone vs a mouse (owner, 2026-09-29: «на телефонах список
 * категорий неудобно нажимать — узкие»). Every option row was `py-1
 * text-[12.5px]` on every screen — about 27 px tall, against the 44 px Apple
 * and 48 dp Android give as the smallest thing a finger hits reliably. Below
 * `md` a row is now at least 44 px with 14 px text; at `md` and up it is
 * exactly what it was, so the desktop dropdown keeps its density.
 */
const ROW_TOUCH = 'min-h-[44px] py-2.5 text-[14px] md:min-h-0 md:py-1 md:text-[12.5px]';
const IKPU_ROW_TOUCH = 'min-h-[44px] py-2 text-[14px] md:min-h-0 md:py-1.5 md:text-[12.5px]';
const MORE_ROW_TOUCH = 'min-h-[44px] py-2.5 text-[14px] md:min-h-0 md:py-2 md:text-[12.5px]';
const MORE_ROW_TOUCH_SM = 'min-h-[44px] py-2.5 text-[14px] md:min-h-0 md:py-1.5 md:text-[12.5px]';

const IKPU_ROW_PAD = 'pl-3 pr-3';
/**
 * Narrow on a phone, full width from `sm` up.
 *
 * 6.5rem is 104px of a 375px screen, and with the checkbox, disclosure and
 * count around it the name was left ~150px — «Архитектурные, инжен…». The code
 * is a scanning aid, not the content, so it yields first. Codes still share a
 * left edge at both sizes, which is what makes the prefix column readable.
 */

/**
 * The ИКПУ code sits immediately before the name, with no reserved width.
 *
 * It used to flow INLINE with the label, so «06.20» and «06.20.10.132» pushed
 * sibling names to different x positions. Reserving a per-depth width fixed
 * that but bought it with a band of dead space — 8-character codes in an
 * 80px column left ~27px of air on every row.
 *
 * Neither is needed: codes at one nesting depth are the same length (all
 * depth-2 codes are `NN.NN.NN`, all depth-3 are `NN.NN.NN.NNN`), so the code
 * self-aligns within a level and the row's indent does the rest. `shrink-0`
 * keeps it from being squeezed by a long name.
 */

function idOf(s: SubjectSelection): string {
  return s.kind === 'ikpu' ? s.code : s.slug;
}

export interface SubjectPickerProps {
  /**
   * `single`: picking a row replaces the selection and closes the dropdown
   * (the catalog's browse control). `multi`: every row stays in the list and
   * shows a checkbox — picking one reports it via `onSelect` without closing,
   * and the CALLER decides what that click means (add, or toggle off if it's
   * already in `selected` — the saved-search builder's "which categories").
   */
  mode: 'single' | 'multi';
  /** The current pick(s). Drives the trigger label (single mode, when
   *  `triggerLabel` is omitted), and row highlighting — in multi mode,
   *  selected rows also sort to the top of their section so a pick is never
   *  scrolled out of view the moment it's made. */
  selected: SubjectSelection[];
  /** Fires on every row click, selected or not — multi mode never removes a
   *  row from the list, so re-clicking a selected row is how the caller finds
   *  out "toggle this off" (compare against `selected` to tell add from
   *  remove; the picker itself doesn't guess). */
  onSelect: (s: SubjectSelection) => void;
  /**
   * Render the current selection as removable chips INSIDE the trigger,
   * scrolling horizontally, instead of leaving the caller to list them above
   * it. Supply this and the caller should drop its own chip list.
   *
   * Callers that omit it keep the plain single-label trigger — the catalog
   * picks one subject at a time, so a chip rail there would be one chip and a
   * lot of chrome.
   */
  onRemove?: (s: SubjectSelection) => void;
  /** Whether a given chip may be removed. A chip whose removal is refused
   *  renders without its ✕ rather than with a dead one. */
  canRemove?: (s: SubjectSelection) => boolean;
  /** The «Все» row that drops the axis entirely. Single mode only — a multi
   *  picker has no single "clear everything" gesture; removal happens via
   *  the caller's own chips. */
  onClear?: () => void;
  /** Which result sections to offer beyond categories. Both default on. */
  sections?: { ikpu?: boolean; niches?: boolean };
  /** The catalog filters every option's live-tender count is computed under
   *  (region, kind, status, budget, deadline, scope, …). Omit for a count
   *  that ignores every filter (e.g. a subscription builder with no active
   *  browsing session). */
  countFilters?: CatalogFilters;
  /** Override the trigger's visible text. Defaults to the current single
   *  selection's name, or «Все». */
  triggerLabel?: string;
  /** Draw the activation-nudge ring (only meaningful while nothing is
   *  selected — mirrors the catalog's `highlightCategory`). */
  highlight?: boolean;
  /** Extra classes on the outer (relative-positioned) wrapper — e.g. the
   *  catalog's grid column span. */
  className?: string;
  /**
   * The catalogue's way out when NO code answers the query: a line «Такого
   * кода нет — искать «{q}» по словам в лотах» that hands the words to this
   * callback (the filter bar puts them into the page's own search). Omit it
   * everywhere a code is REQUIRED — the saved-search sheet — and the picker
   * shows a hint to write one word instead. See `lib/ikpu-search-fallback.ts`.
   */
  onSearchWords?: (q: string) => void;
}

/**
 * A trigger button + searchable dropdown over category / ИКПУ / niche.
 *
 * The dropdown panel is rendered through a `Portal` to `document.body`,
 * position-tracked from the trigger's `getBoundingClientRect()`, rather than
 * a plain `absolute` child. A plain-`absolute` panel is clipped by the first
 * `overflow` ancestor — fine on the catalog page (no such ancestor) but the
 * saved-search builder is a bottom sheet, and `SheetContent`'s bottom variant
 * is itself `overflow-y-auto` (see `ui/sheet.tsx`). Mirrors the DS `Select`'s
 * documented guarantee ("never clips inside the filter bottom-sheet") rather
 * than reintroducing the failure mode it was built to avoid.
 *
 * The panel's own height is bounded to whatever room is actually left below
 * the trigger (see `place()`'s `maxHeight` math) and scrolls internally
 * within that bound. `position: fixed` content is invisible to page scroll —
 * nothing grows the document to reveal a tail that spills past the viewport
 * edge — so an unbounded panel opened from a trigger sitting low in a short
 * sheet could render options nothing could reach. Bounding + internal scroll
 * keeps every row reachable regardless of where the trigger lands.
 */
/**
 * The picker's trigger, with the current selection rendered as removable chips
 * inside it.
 *
 * Two things make this a `div` with buttons in it rather than one big button:
 * a chip's ✕ is itself a button, and nesting interactive elements is invalid.
 * The open affordance is therefore its own button filling the remaining space.
 *
 * The chips WRAP to as many rows as it takes, so every selection stays visible
 * and one tap from removal — the field grows in height rather than hiding the
 * overflow. (An earlier version scrolled them horizontally to stay compact, but
 * testers couldn't see or remove the selections that scrolled off.) The count
 * is still pinned OUTSIDE the wrapping area as a quick at-a-glance total.
 */
function ChipRailTrigger({
  chips,
  onRemove,
  canRemove,
  removeLabel,
  onOpen,
  label,
  ...rest
}: {
  chips: SubjectSelection[];
  onRemove?: (s: SubjectSelection) => void;
  /** Whether a given chip may be removed. A chip whose removal is refused
   *  renders without its ✕ rather than with a dead one. */
  canRemove?: (s: SubjectSelection) => boolean;
  removeLabel: (name: string) => string;
  onOpen: () => void;
  label: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>) {
  const { className, ...divProps } = rest as { className?: string };
  if (chips.length === 0 || !onRemove) {
    return (
      <button type="button" onClick={onOpen} className={className} {...divProps}>
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        <span className="shrink-0 text-[10px] text-ink-muted">⌄</span>
      </button>
    );
  }
  const { 'aria-label': ariaLabel, ...plainDivProps } = divProps as Record<string, unknown>;
  return (
    /* The WHOLE trigger opens the panel, not just the ⌄ — the chip rail is
       most of its width and clicking it did nothing. A chip's ✕ stops
       propagation so removing one does not also open the dropdown. */
    <div
      // Chips wrap here, so neutralise the caller's single-line fixed height →
      // min-height (the field grows with the rows); py gives wrapped rows
      // breathing room. Keep the caller's `items-center` so the chips and ⌄ stay
      // vertically centred — against a single row or a multi-row block.
      // (The compact filter-bar trigger keeps the caller's height as-is, since it
      // never renders the chip rail.)
      className={`${(className ?? '')
        .replace('h-11', 'min-h-11')
        .replace('md:h-9', 'md:min-h-9')} cursor-pointer py-1.5`}
      {...plainDivProps}
      aria-label={undefined}
      onClick={onOpen}
    >
      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
        {chips.map((c) => (
          <span
            key={`${c.kind}-${idOf(c)}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-brand/25 bg-brand-light px-1.5 py-0.5 text-[11px] text-brand"
          >
            <span className="max-w-[9rem] truncate">{c.name}</span>
            {(canRemove?.(c) ?? true) && (
              <button
                type="button"
                aria-label={removeLabel(c.name)}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(c);
                }}
                className="shrink-0 text-ink-muted transition-colors hover:text-brand"
              >
                ✕
              </button>
            )}
          </span>
        ))}
      </div>
      <button
        type="button"
        // Inside the clickable container: without this the ⌄ fires both
        // handlers and the panel toggles twice, i.e. never opens.
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        aria-label={(ariaLabel as string) ?? label}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] text-ink-muted transition-colors hover:bg-brand/10 hover:text-brand"
      >
        ⌄
      </button>
    </div>
  );
}

/**
 * The picker's one arrow — a short shaft plus a head, stroked so it carries the
 * same visual weight as the text beside it. A filled ▸ read as a bullet rather
 * than as a control.
 *
 * `rotated` turns it a quarter turn for an open section; the "показать ещё"
 * rows use it un-rotated.
 */
function PickerArrow({ rotated = false }: { rotated?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform duration-150 ${rotated ? 'rotate-90' : ''}`}
    >
      <path d="M4.5 8h6" />
      <path d="M8 5.5 10.5 8 8 10.5" />
    </svg>
  );
}

export function SubjectPicker({
  mode,
  selected,
  onSelect,
  onRemove,
  canRemove,
  onClear,
  sections,
  countFilters = {},
  triggerLabel,
  highlight = false,
  className = '',
  onSearchWords,
}: SubjectPickerProps) {
  const { t, locale } = useLocale();
  const showIkpu = sections?.ikpu !== false;
  const showNiches = sections?.niches !== false;
  const listboxId = useId();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Where the panel is portaled TO.
  //
  // ⚠️ NOT unconditionally `document.body`. Inside a modal Radix dialog — which
  // is exactly where the saved-search builder puts this — Radix marks
  // everything outside its content `aria-hidden` and `pointer-events: none`.
  // A panel under `document.body` is outside that content, so it renders,
  // takes no clicks, and the mousedown lands on the page beneath instead:
  // the dropdown closes and the pick never happens. Portaling into the dialog
  // keeps it inside the interactive subtree (and inside the a11y tree).
  //
  // Still portaled rather than rendered in place: the sheet is a scrolling,
  // `overflow-y-auto` container, which would clip an inline panel.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /*
   * On a touch screen the search box is NOT focused for the reader (owner,
   * 2026-09-29, measured on a Redmi 15C at 360×691): focusing it on open raised
   * the keyboard over half the list, leaving ~5 category rows to aim at — the
   * «неудобно нажимать» that started this. Most people on a phone browse the
   * list rather than type; one tap on the box still opens the keyboard. With a
   * mouse nothing changes: focus on open is what makes typing immediate there.
   */
  const coarsePointer = useCoarsePointer();
  const focusSearch = () => {
    if (!coarsePointer) requestAnimationFrame(() => inputRef.current?.focus());
  };
  const [pos, setPos] = useState<{
    top: number | null;
    bottom: number | null;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  // The on-screen keyboard shrinks (and offsets) the VISUAL viewport on
  // mobile without changing `window.innerHeight` — a `top`/`maxHeight` sized
  // off `innerHeight` alone goes stale the moment the search input the panel
  // itself contains grabs focus and the keyboard opens.
  const viewport = useVisualViewport();

  // Debounce the search so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(id);
  }, [query]);

  // Progressive-disclosure caps: reset on every open and whenever the query
  // changes, so a fresh search starts at page one rather than inheriting
  // however far the previous one was expanded.
  const [catLimit, setCatLimit] = useState(CAT_PAGE);
  const [ikpuLimit, setIkpuLimit] = useState(IKPU_PAGE);
  const [nicheLimit, setNicheLimit] = useState(NICHE_PAGE);
  // Which BROWSE-mode ИКПУ sections (depth-0 rows) are expanded — keyed by
  // the section's own code. Reset alongside the paging caps above: a query
  // change flattens the grouping entirely (see `browsingIkpu` below), and
  // clearing the query back to browse mode should restore every section
  // collapsed, not resume wherever it was left.
  const [expandedIkpuSections, setExpandedIkpuSections] = useState<Set<string>>(new Set());
  useEffect(() => {
    setCatLimit(CAT_PAGE);
    setIkpuLimit(IKPU_PAGE);
    setNicheLimit(NICHE_PAGE);
    setExpandedIkpuSections(new Set());
  }, [open, query]);
  function toggleIkpuSection(code: string) {
    setExpandedIkpuSections((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  // Close the panel on outside click. The portaled panel isn't a DOM
  // descendant of `boxRef` (it lives under document.body), so a click inside
  // it must ALSO count as "inside" or every pick would close-then-reopen.
  useEffect(() => {
    if (!open) {
      setPortalTarget(null);
      return;
    }
    setPortalTarget(
      (boxRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? document.body,
    );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (boxRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Track the trigger's screen position so the portaled panel can anchor
  // itself under it, and re-anchor on scroll (capture-phase: a bottom sheet
  // scrolls an inner div, not the window, and scroll doesn't bubble — the
  // capture listener still sees it), on resize, and on the trigger's OWN
  // size changing (a `ResizeObserver` — several selected-category chips
  // wrapping onto a new line reflows the trigger without firing `resize` or
  // `scroll` at all).
  //
  // `maxHeight` is bounded to the space actually left in the viewport, NOT a
  // flat constant. A `position: fixed` element is invisible to the page's
  // own scroll — nothing grows the document to reveal whatever spills past
  // the viewport edge, and there's no ancestor scrollbar to reach it with
  // either. A trigger sitting low in a short bottom sheet could open a panel
  // whose tail is simply unreachable; clamping to what's visible and
  // scrolling INSIDE that bound keeps every row reachable.
  //
  // Flips to open UPWARD when there's meaningfully more room above the
  // trigger than below (spaceBelow under the floor) — without this, a
  // trigger near the bottom of a short sheet always got squeezed into the
  // 160px floor even when the whole rest of the sheet, above it, was empty.
  //
  // The space is measured against `0 … innerHeight`, EXCEPT while a keyboard
  // is up on a touch screen (`keyboardVisibleBand`): then against the part of
  // the layout viewport that is actually visible, which is never larger. That
  // band is in the same coordinates as `getBoundingClientRect()`, and the
  // effect re-runs on every `visualViewport` resize/scroll (the `viewport`
  // dependency) — so the panel shrinks when the keyboard opens and grows back
  // when it closes.
  //
  // ⚠️ With the keyboard up there may be no good side at all. Redmi 15C,
  // 2026-10-16, «мебель» typed in the catalogue: 338px visible, the trigger at
  // 182–224, so 108px below it and 176px above — the panel flipped up and
  // showed the search box plus ~2.5 rows, and not one code. When neither side
  // has `comfort` px, the panel takes the whole visible band instead, covering
  // its own trigger: the search box it holds is what the reader is using, and
  // closing the keyboard brings the ordinary placement back.
  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 6;
      const floor = 160; // stays usable even at the very edge of the viewport
      const ceiling = 420; // don't stretch needlessly tall just because room exists
      const comfort = 240; // the search box and ~4 rows
      const viewportH = window.innerHeight;
      const host =
        portalTarget && portalTarget !== document.body
          ? portalTarget.getBoundingClientRect()
          : null;
      const band = keyboardVisibleBand(coarsePointer);
      // In a sheet, only the sheet's own box can show the panel: the panel
      // scrolls with the sheet and is CLIPPED by it (see the re-basing below).
      const visTop = band ? Math.max(band.top, host ? host.top : band.top) : 0;
      const visBottom = band
        ? Math.max(visTop, Math.min(band.bottom, host ? host.bottom : band.bottom))
        : viewportH;
      // A trigger the keyboard hides must not drag the panel under it too.
      const below = band ? Math.max(rect.bottom, visTop) : rect.bottom;
      const above = band ? Math.min(rect.top, visBottom) : rect.top;
      const spaceBelow = visBottom - below - margin;
      const spaceAbove = above - visTop - margin;
      const cover = band != null && Math.max(spaceBelow, spaceAbove) < comfort;
      const openUp = !cover && spaceBelow < floor && spaceAbove > spaceBelow;
      const maxHeight = cover
        ? Math.max(0, Math.min(ceiling, visBottom - visTop - 2 * margin))
        : Math.max(floor, Math.min(openUp ? spaceAbove : spaceBelow, ceiling));
      // ⚠️ Re-base the coordinates onto the CONTAINING BLOCK, which is not
      // always the viewport.
      //
      // The panel is `position: fixed`, and `getBoundingClientRect()` is
      // viewport-relative, so `left/top` normally line up. But a `fixed`
      // element resolves against the nearest ancestor with a transform,
      // filter or `will-change` for those — and the bottom sheet this lives
      // inside carries `will-change-transform` (`ui/sheet.tsx`) for its slide
      // animation. That makes the SHEET the containing block, and the same
      // viewport numbers then measure from the sheet's own box: on a 1600px
      // screen the panel landed ~520px too far right, mostly off-screen.
      //
      // So when we are portaled into a dialog rather than into `document.body`,
      // subtract that element's origin. `document.body` is not a containing
      // block, so the offset is zero there and the maths is unchanged.
      //
      // ⚠️ …AND add back its SCROLL. The sheet is also the scroll container,
      // and a `fixed` box whose containing block is a scroller scrolls with its
      // content like an `absolute` one — measured in headless Chromium
      // 2026-10-17: `top: 50px` drew at sheet top + 51, and at + 51 − 100 once
      // the sheet was scrolled 100px. Without this the panel sat `scrollTop`
      // px above its trigger in a scrolled sheet, over the trigger itself
      // (Redmi 15C, «Настроить поиск», 2026-10-16). It is also clipped by the
      // sheet: the part of a `top: -50px` box above the sheet does not hit-test.
      const sx = host && portalTarget ? portalTarget.scrollLeft : 0;
      const sy = host && portalTarget ? portalTarget.scrollTop : 0;
      const dx = host ? host.left - sx : 0;
      const dy = host ? host.top - sy : 0;
      // The containing block's bottom edge, in the same terms.
      const cbBottom = host ? host.bottom - sy : viewportH;
      setPos({
        left: rect.left - dx,
        width: rect.width,
        maxHeight,
        top: openUp ? null : (cover ? visTop : below) + margin - dy,
        bottom: openUp ? cbBottom - (above - margin) : null,
      });
    }
    place();
    const ro = new ResizeObserver(place);
    if (boxRef.current) ro.observe(boxRef.current);
    // The sheet itself resizing (its keyboard cap set or lifted) moves the
    // trigger without any `scroll` or window `resize` to say so.
    if (portalTarget && portalTarget !== document.body) ro.observe(portalTarget);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, portalTarget, viewport.height, viewport.offsetTop, coarsePointer]);

  /**
   * ⚠️ A UNION in single mode is shown but never marked.
   *
   * The catalog is single-select — a click REPLACES the selection — yet it can
   * arrive holding several categories, because a saved-search link joins them
   * (`savedSearchToCatalogPath`) and `buildWhere` reads `?category=a,b,c` as a
   * union. Tinting all three would advertise a toggle the mode does not have,
   * and the click a user reads as "remove this one" would silently drop the
   * other two.
   *
   * So the trigger still LISTS every applied category (the page really is
   * filtered by all of them) while the rows stay unmarked. If the catalog ever
   * becomes genuinely multi-select, deleting this guard restores the marking.
   */
  const unionInSingleMode = mode === 'single' && selected.length > 1;
  const isPicked = (kind: SubjectSelection['kind'], id: string) =>
    !unionInSingleMode && selected.some((s) => s.kind === kind && idOf(s) === id);

  /**
   * An ИКПУ row is COVERED when it is picked itself, or when any ancestor is.
   *
   * Picking `71` already matches everything beneath it — `ikpuFilterOr`
   * matches a code and its descendants, and the counts are hierarchical
   * (`71` = 358 contains `71.12` = 331). So a parent's pick genuinely selects
   * its children; showing them as unselected was the display lying about what
   * the alert will match.
   *
   * They are covered, NOT stored: adding every descendant to the criteria
   * would change nothing about matching while bloating the saved blob, and
   * `71` would then disagree with itself the moment a new sub-code is
   * harvested under it. Containment comes from the API's `parentCode` chain,
   * never from parsing the dotted string.
   */
  const ikpuChildrenOf = (code: string): IkpuItem[] =>
    topIkpuAll.filter((r) => r.parentCode === code);

  const ikpuCovered = (item: IkpuItem): boolean => {
    if (isPicked('ikpu', item.code)) return true;
    const byCode = new Map(topIkpuAll.map((r) => [r.code, r]));
    let cursor = item.parentCode ? byCode.get(item.parentCode) : undefined;
    while (cursor) {
      if (isPicked('ikpu', cursor.code)) return true;
      cursor = cursor.parentCode ? byCode.get(cursor.parentCode) : undefined;
    }
    // …and upwards: a parent whose every child is covered is itself covered.
    // Unticking ONE of them un-covers the parent, which is the same rule read
    // backwards — no separate bookkeeping, so the two can never disagree.
    const kids = ikpuChildrenOf(item.code);
    return kids.length > 0 && kids.every(ikpuCovered);
  };

  /** Every descendant of a code that is EXPLICITLY in the selection. */
  const pickedDescendants = (code: string): IkpuItem[] => {
    const out: IkpuItem[] = [];
    const walk = (c: string) => {
      for (const kid of ikpuChildrenOf(c)) {
        if (isPicked('ikpu', kid.code)) out.push(kid);
        walk(kid.code);
      }
    };
    walk(code);
    return out;
  };

  // Multi mode's checkbox: `null` (renders nothing) outside multi mode, so
  // single-mode markup is byte-identical to before this existed — the
  // catalog's own regression suite pins that shape.
  /*
   * Selection is a TINTED ROW, not a checkbox.
   *
   * The checkbox column was removed on request: at 70 sections plus their
   * children it was a lot of chrome, and the brand tint plus a bold label
   * already say "picked". Hover deliberately stays neutral (`bg-bone`) so the
   * two states never read as the same thing.
   */

  // Commit a pick: report it, reset the search text, and — single mode only
  // — close. Multi stays open (the whole point is picking more than one) and
  // refocuses the search box so the next pick doesn't need a re-click.
  function pick(s: SubjectSelection) {
    if (s.kind === 'ikpu') {
      const item = topIkpuAll.find((r) => r.code === s.code);
      const explicit = isPicked('ikpu', s.code);
      const kids = item ? ikpuChildrenOf(item.code) : [];
      const coveredByKids = !explicit && kids.length > 0 && kids.every(ikpuCovered);
      if (explicit || coveredByKids) {
        // Turning a parent OFF takes its children with it. Otherwise unticking
        // `71` would leave `71.12` behind and the row would stay green — the
        // user would have unselected something and seen nothing change.
        for (const d of pickedDescendants(s.code)) {
          onSelect({ kind: 'ikpu', code: d.code, name: d.name });
        }
        // `coveredByKids` means the parent was never in the selection itself,
        // so there is nothing of its own left to toggle.
        if (explicit) onSelect(s);
        setQuery('');
        if (mode === 'single') setOpen(false);
        else focusSearch();
        return;
      }
    }
    onSelect(s);
    setQuery('');
    if (mode === 'single') {
      setOpen(false);
    } else {
      focusSearch();
    }
  }
  function clearAxis() {
    onClear?.();
    setOpen(false);
    setQuery('');
  }

  const hasSelection = selected.length > 0;
  // Several selections without a chip rail (the catalog: single-select by
  // click, but a saved-search link can arrive carrying a union) get all their
  // names, not just the first — showing one of three silently misreports what
  // the page is filtered by.
  // With nothing picked, the trigger normally reads «Все» — correct for a
  // returning user browsing unfiltered. But while the activation nudge is on
  // (a first-timer who hasn't picked yet), «Все» reads as "nothing to do" and
  // people just scroll; there the orange ring already draws the eye, so the
  // label becomes the matching call to action «Выбрать категорию». An explicit
  // `triggerLabel` from the caller still wins over both.
  const label =
    triggerLabel ??
    (selected.length > 1
      ? selected.map((sel) => sel.name).join(', ')
      : (selected[0]?.name ?? t(highlight ? 'filter.selectCategory' : 'filter.all')));

  const { data: categories } = useQuery({
    queryKey: ['catalog', 'categories', locale, countFilters],
    queryFn: () => fetchCategories(locale, countFilters),
  });

  const browsing = query.trim().length < 1;

  // Results — categories (filtered client-side) + ИКПУ (server-ranked). While
  // BROWSING (no query) the list is sorted by live-tender count so the biggest
  // categories lead, then capped (see catShown) so the ИКПУ section stays near
  // the top. While SEARCHING, keep filter/relevance order and show every match.
  //
  // Multi mode never removes a picked row — it's a checklist, not an add-once
  // list, and a picker that makes its own selections disappear can't be used
  // to review or undo them. Instead the picked ones float to the TOP of
  // whatever the browse/search order already produced: `Array#sort` is
  // stable, so a "picked first" comparator over an already-sorted/filtered
  // list preserves each group's internal order rather than reshuffling it.
  const catResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = categories ?? [];
    const ordered =
      q.length < 1
        ? [...all].sort((a, b) => b.count - a.count)
        : all.filter((c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q));
    if (mode !== 'multi') return ordered;
    return [...ordered].sort(
      (a, b) => Number(isPicked('category', b.slug)) - Number(isPicked('category', a.slug)),
    );
  }, [query, categories, mode, selected]);
  // Browse: reveal only the first `catLimit` rows; search: show all matches.
  // Because picked rows sort first, a pick never gets capped out of view.
  const catShown = browsing ? catResults.slice(0, catLimit) : catResults;
  const catMoreCount = browsing ? catResults.length - catShown.length : 0;

  const { data: ikpuHits, isFetching: ikpuFetching } = useQuery({
    queryKey: ['filter-niche-ikpu', debounced, locale, countFilters],
    queryFn: () => searchIkpu(debounced, locale, 20, countFilters),
    enabled: showIkpu && debounced.length >= 2,
    staleTime: 60_000,
  });
  // Curated Niche (Spec #3) hits — searched in parallel with the raw ИКПУ
  // codes, rendered ABOVE them (a niche is a curated bundle of codes, so it's
  // the more useful match when both hit). Search-as-you-type only — there's
  // no "top niches" browse facet (unlike ИКПУ's `fetchIkpuCodes`).
  const { data: nicheHits } = useQuery({
    queryKey: ['filter-niche-search', debounced, locale, countFilters],
    queryFn: () => searchNiches(debounced, locale, countFilters),
    // Runs while BROWSING too (blank query), not only from 2 characters. The
    // API now returns every niche for a blank term, so this is the niche
    // counterpart of ИКПУ's browse facet — without it a user has to already
    // know a niche's name to discover one exists.
    enabled: showNiches,
    staleTime: 60_000,
  });
  const nicheResultsAll = showNiches ? (nicheHits ?? []) : [];
  // Same "picked floats to the top, nothing is ever hidden" rule as
  // categories above.
  const nicheOrdered =
    mode === 'multi'
      ? [...nicheResultsAll].sort(
          (a, b) => Number(isPicked('niche', b.slug)) - Number(isPicked('niche', a.slug)),
        )
      : nicheResultsAll;
  // Paged, not a hard 6. There are 23 niches in production and the cap hid 17
  // of them behind nothing at all — no "показать ещё", no indication the list
  // continued. Same progressive-disclosure treatment the other two sections
  // already had.
  const nicheResults = nicheOrdered.slice(0, nicheLimit);
  const nicheMoreCount = nicheOrdered.length - nicheResults.length;

  // Top ИКПУ niches (ranked by live-tender count, nested depth-first
  // server-side) — shown on OPEN so the control browses like a select, not
  // only search-as-you-type.
  // ⚠️ `countFilters` in BOTH the key and the call — the categories query two
  // hooks up has always passed them, and this one did not, so the two halves of
  // the same popover answered the same question with different numbers: with
  // region + kind narrowing the page to 71 tenders, «Строительство зданий»
  // correctly dropped to 40 while the ИКПУ section below it still advertised
  // the whole catalogue's 409. Omitting it from the key is the subtler half of
  // that bug — the first unfiltered response would be cached and then served to
  // every later filter state.
  const { data: ikpuFacets } = useQuery({
    queryKey: ['ikpu-facets', locale, countFilters],
    queryFn: () => fetchIkpuCodes(locale, countFilters),
    enabled: showIkpu,
    staleTime: 5 * 60_000,
  });
  // Already ranked AND depth-first ordered server-side (`markIkpuNesting` in
  // `catalog.service.ts`) — re-sorting by count here, flat, would interleave
  // parents and children by their absolute counts and destroy the section
  // grouping the browse view below depends on.
  const topIkpuAll: IkpuItem[] = showIkpu ? (ikpuFacets ?? []) : [];
  const browsingIkpu = query.trim().length < 2;

  /*
   * BROWSE-mode sections: a depth-0 row starts a new top-level ИКПУ section;
   * every row after it, up to the next depth-0 row, is — by construction,
   * since the API hands back the facet list depth-first — (transitively) its
   * subtree. No code-string parsing: purely the `depth` field the API
   * computed alongside the counts. Collapsed by default (591 live codes
   * across ~70 sections is not something anyone scrolls through flat); each
   * section's own disclosure expands it in place, while the row's LABEL
   * still SELECTS the section — the same `pick` every ИКПУ row uses,
   * subscribing to a whole section is common and legitimate on its own.
   */
  const ikpuSections = useMemo(() => {
    const out: { root: IkpuItem; children: IkpuItem[] }[] = [];
    for (const item of topIkpuAll) {
      if ((item.depth ?? 0) === 0) out.push({ root: item, children: [] });
      else out[out.length - 1]?.children.push(item);
    }
    // Picked sections float to the top, like the categories and niches do.
    //
    // Only SECTIONS move — a section's children keep their depth-first order
    // inside it. Floating a picked child to the top of the whole list would
    // tear it away from the parent whose count its «N из M» share is quoted
    // against, which is the one thing that ordering has to preserve.
    const picked = (r: IkpuItem) => (isPicked('ikpu', r.code) ? 0 : 1);
    return out.sort((a, b) => picked(a.root) - picked(b.root));
  }, [topIkpuAll, selected]);
  // Sections revealed `ikpuLimit` at a time via "показать ещё" — the same
  // progressive-disclosure cap the flat search list below uses, just counting
  // SECTIONS instead of rows.
  const ikpuSectionsShown = ikpuSections.slice(0, ikpuLimit);
  const ikpuMoreSectionsCount = ikpuSections.length - ikpuSectionsShown.length;

  /*
   * SEARCH flattens the grouping — ranked matches exactly as before, no
   * collapsible sections — revealed `ikpuLimit` at a time via "показать ещё".
   * The API returns a code's whole subtree depth-first, so a hard slice would
   * spend the whole page descending one branch and never reach its siblings —
   * see the ikpu-nesting regression test for the exact shape that broke.
   */
  // ИКПУ rows are NOT reordered by pick state, unlike categories/niches: the
  // list is a parent→children tree flattened depth-first (both here and
  // within an expanded browse section below), and the indent / "N из M"
  // share only reads correctly in that order — floating a picked child above
  // its (unpicked) parent would orphan it visually. Picked rows still get the
  // checkbox, they just don't relocate.
  const ikpuHitsAll: IkpuItem[] = showIkpu ? (ikpuHits ?? []) : [];
  const ikpuResults = ikpuHitsAll.slice(0, ikpuLimit);
  const ikpuMoreCount = ikpuHitsAll.length - ikpuResults.length;
  // Only once the typed text has settled and the codes for IT are in, so
  // neither the way out nor the hint flashes mid-keystroke.
  const ikpuSettled = showIkpu && debounced === query.trim() && !ikpuFetching;
  const offerWordsSearch =
    onSearchWords !== undefined && ikpuSettled && noIkpuCodeFound(debounced, ikpuHitsAll);

  /**
   * The shared row renderer — used for a browse-mode section's root AND its
   * children, and for every flattened search hit. Identical markup in every
   * case (the `ikpu-nesting` regression suite pins this shape for the search
   * path); only the disclosure wrapper around a browse section's ROOT differs,
   * added by the caller, not here.
   */
  function ikpuRow(
    item: IkpuItem,
    depthOverride?: number,
    padOverride?: string,
    /** The section wrapper already paints the picked tint (it has to — the
     *  disclosure column sits outside this row). `brand-dim` is translucent,
     *  so painting it again here stacked two greens on one row. */
    tintedByParent?: boolean,
    /**
     * Set only for a BROWSE-mode section root that has children — wires
     * `ArrowRight`/`ArrowLeft` on the focused row to the same expand/collapse
     * the disclosure glyph triggers (the ARIA treegrid convention). Click
     * behaviour is untouched: the row is `role="option"` inside
     * `role="listbox"`, and a listbox's own keyboard contract has no
     * horizontal-arrow semantics to collide with — see this file's report
     * for why the roles themselves are left alone rather than moved to
     * `treeitem`/`tree`, which is the more "honest" fit for a collapsible
     * hierarchy but a much larger a11y change than this ships.
     */
    sectionControl?: { expanded: boolean; onToggle: () => void },
  ) {
    const depth = depthOverride ?? item.depth ?? 0;
    /*
     * A code search returns a code AND its sub-codes, and the counts are
     * HIERARCHICAL: 49.41 = 41 already contains 49.41.19.900 = 34, which
     * already contains 49.41.19.90000002 = 4. Only siblings sum. Listed flat
     * and identically styled, the column invites a reader to add it up, get
     * 45 against a stated 41, and conclude the numbers are broken — they
     * aren't, the presentation was.
     *
     * So a contained row is inset one step per level and states its own
     * share as «4 из 34». The API supplies the relationship; nothing here
     * re-reads the code string. Descendants are never hidden — they are
     * separately selectable (via expand or search), and a code the user just
     * typed must not appear to be missing.
     */
    const partOf = item.parentCode ? item.parentCount : null;
    /*
     * ⚠️ A borrowed name must not be printed as this code's own. The API
     * synthesizes a group row for a typed prefix that has no reference row,
     * naming it from what its children share — and when they share nothing,
     * from the nearest ancestor. The row still exists and is still
     * selectable; it just says what it actually is: every code under that
     * prefix.
     */
    const inherited = item.nameInheritedFrom ?? null;
    const label2 = inherited ? t('filter.ikpu.allUnder', { code: item.code }) : item.name;
    /*
     * A label recovered from a lot title is the right ballpark and not the
     * code's own name. Dimmed with a «~» so a reader can tell a classifier
     * name from a recovered one.
     */
    const approximate = !inherited && item.nameIsApproximate === true;
    // Which top-level section a row belongs to — set by the API only on
    // SEARCH hits (see `IkpuItem.sectionCode`), so a bare code like
    // "45.31.1" is locatable once search flattens the collapsible grouping.
    // Falsy (undefined) on browse rows and on a row that IS its own section,
    // where it would just repeat the row's own name.
    const showSection = item.sectionCode && item.sectionCode !== item.code;
    return (
      <button
        key={`ikpu-${item.code}`}
        type="button"
        role="option"
        aria-selected={ikpuCovered(item)}
        onClick={() => pick({ kind: 'ikpu', code: item.code, name: item.name })}
        onKeyDown={
          sectionControl
            ? (e) => {
                if (e.key === 'ArrowRight' && !sectionControl.expanded) {
                  e.preventDefault();
                  sectionControl.onToggle();
                } else if (e.key === 'ArrowLeft' && sectionControl.expanded) {
                  e.preventDefault();
                  sectionControl.onToggle();
                }
              }
            : undefined
        }
        title={
          inherited
            ? `${item.code} — ${t('filter.ikpu.allUnder', { code: item.code })} (${inherited} ${item.name})`
            : `${item.code} ${item.name}`
        }
        data-depth={depth}
        className={`flex w-full items-start gap-2 ${IKPU_ROW_TOUCH} text-left transition-colors ${
          padOverride ?? IKPU_ROW_PAD
        } ${
          ikpuCovered(item)
            ? `text-brand ${tintedByParent ? '' : 'bg-brand-dim hover:bg-brand-dim'}`
            : 'text-ink hover:bg-bone'
        }`}
      >
        {/* Code ABOVE the title, not beside it.
            Side by side, a fixed code column left the title ~18-22 characters
            at a phone's 320px and «Услуги по инженерно-техническому проекти…»
            told the user nothing. Stacked, the title gets the full row.
            The height that costs is bounded: sections are collapsed by
            default and only one is open at a time, so the tall rows are the
            ones the user deliberately asked to see. */}
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="font-mono text-[10px] tabular-nums text-ink-muted sm:text-[10.5px]">
            {showSection && (
              <span className="mr-1 not-italic text-ink-muted">
                {item.sectionName ?? item.sectionCode} ›
              </span>
            )}
            {item.code}
          </span>
          <span className="min-w-0">
            {approximate && (
              <span aria-hidden="true" className="mr-0.5 text-ink-muted">
                ~
              </span>
            )}
            <span className={approximate ? 'text-ink-soft' : undefined}>{label2}</span>
          </span>
        </span>
        {/*
         * Rendered even at ZERO, deliberately — see the catalog's original
         * comment: hiding it read as "count unknown" once these counts
         * started honouring the active filters.
         */}
        <span
          className={`mt-px shrink-0 whitespace-nowrap text-right text-[11px] tabular-nums text-ink-muted ${
            item.count > 0 ? '' : 'opacity-60'
          }`}
        >
          {partOf == null
            ? item.count
            : t('filter.ikpu.countOfParent', { n: item.count, total: partOf })}
        </span>
      </button>
    );
  }

  // Kazakhstan, no query typed: the niches lead (see IS_KZ). `browsingIkpu` is
  // this picker's own browse-mode flag for the code list, so the niches and the
  // codes switch modes together; once something is typed the order is the
  // classic one everywhere.
  const nichesFirst = IS_KZ && browsingIkpu;

  const nicheSection = (
    <>
      {/* Curated niches (Spec #3) — a curated bundle of codes, tagged with a
          «ниша» badge and reusing the ИКПУ row markup. Uzbekistan: LAST, under
          the raw ИКПУ codes — they used to sit above them on the reasoning that
          a curated bundle beats a raw code when both match, but the ИКПУ tree is
          what people scan there, and a third heading wedged between the
          categories and that tree pushed it below the fold. Kazakhstan, BROWSING
          (`nichesFirst`): FIRST — see IS_KZ. Typing is the same everywhere. */}
      {nicheResults.length > 0 && (
        <div className="border-t border-edge bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
          {t('niche.section')}
        </div>
      )}
      {nicheResults.map((item) => (
        <button
          key={`niche-${item.slug}`}
          type="button"
          role="option"
          aria-selected={isPicked('niche', item.slug)}
          onClick={() => pick({ kind: 'niche', slug: item.slug, name: item.name })}
          title={item.name}
          className={`flex w-full items-center gap-2 px-3 ${ROW_TOUCH} text-left transition-colors ${
            isPicked('niche', item.slug)
              ? 'bg-brand-dim text-brand hover:bg-brand-dim'
              : 'text-ink hover:bg-bone'
          }`}
        >
          <span className="min-w-0 flex-1 truncate">
            {/* The badge is decoration; the label is the option. Without
                a separator the accessible name concatenated into
                «НИШАСтроительство зданий» — one garbled word to a screen
                reader, while sighted users see a clean pill. A trailing
                space inside the badge is not enough (collapsed in the
                a11y tree), so the separator is a real, hidden one. */}
            <span
              aria-hidden="true"
              className="mr-1.5 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em] text-brand"
            >
              {t('niche.badge')}
            </span>
            <span className="sr-only">{t('niche.badge')}: </span>
            {item.name}
          </span>
          {/* Rendered even at ZERO — see the ИКПУ row below for why. */}
          <span
            className={`shrink-0 text-[11px] text-ink-muted ${
              item.tenderCount > 0 ? '' : 'opacity-60'
            }`}
          >
            {item.tenderCount}
          </span>
        </button>
      ))}
      {nicheMoreCount > 0 && (
        <button
          type="button"
          onClick={() => setNicheLimit((n) => n + NICHE_PAGE)}
          className={`flex w-full items-center gap-1.5 px-3 ${MORE_ROW_TOUCH_SM} text-left font-medium text-brand transition-colors hover:bg-bone`}
        >
          <PickerArrow />
          {t('filter.more.niches', { count: nicheMoreCount })}
        </button>
      )}
    </>
  );

  return (
    <div ref={boxRef} className={`relative w-full scroll-mt-20 ${className}`}>
      <ChipRailTrigger
        chips={onRemove ? selected : []}
        onRemove={onRemove}
        canRemove={canRemove}
        removeLabel={(name) => t('filter.chip.remove', { name })}
        onOpen={() => {
          /*
           * On a touch screen, bring the trigger to the top before the panel
           * opens. The panel is sized to the space BELOW the trigger (`place()`),
           * and in the catalog the trigger sits under the onboarding card —
           * measured on a Redmi 15C (360×691): ~200px left, 4–5 rows of the
           * list visible. From the top it gets the full 420px ceiling, ~9 rows.
           * `scroll-mt-20` keeps it clear of the sticky header; `place()` already
           * re-runs on scroll, so the panel follows.
           */
          if (!open && coarsePointer) boxRef.current?.scrollIntoView?.({ block: 'start' });
          setOpen((v) => !v);
          focusSearch();
        }}
        // Falls back to the catalog's generic "category or item" wording
        // (reusing `filter.niche`, not inventing a parallel string) — but
        // when a caller overrides the VISIBLE label via `triggerLabel`, the
        // accessible name must match it too, or a screen-reader user hears a
        // name ("Категория или товар") that names nothing on screen while
        // sighted users read something else ("Добавить категорию").
        aria-label={triggerLabel ?? t('filter.niche')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        className={`flex h-11 w-full items-center gap-1.5 rounded-md border px-3 text-[15px] font-medium transition-colors md:h-9 md:text-[13px] ${
          hasSelection
            ? onRemove
              ? // chip rail: the chips carry the tint, so the trigger is the
                // neutral ground they sit on
                'border-brand/40 bg-bone-surface text-ink'
              : 'border-brand/40 bg-brand-dim text-brand'
            : highlight
              ? 'border-orange bg-bone-surface text-ink ring-[3px] ring-orange/25'
              : 'border-edge bg-bone-surface text-ink hover:border-brand/30'
        }`}
        label={label}
      />

      {open &&
        pos &&
        portalTarget &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom ?? 0 }),
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
            }}
            className="z-[60] flex min-w-[300px] flex-col overflow-hidden rounded-md border border-edge bg-bone-surface p-1 shadow-m-md"
          >
            <div className="shrink-0 p-1">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('filter.niche.search')}
                aria-label={t('filter.niche.search')}
                autoComplete="off"
                className="h-10 w-full rounded-md border border-edge bg-bone px-3 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand md:h-9 md:text-[13px]"
              />
            </div>
            {/* `min-h-0` is load-bearing: without it a flex child refuses to
                shrink below its content size, and the list would push the
                panel taller than `maxHeight` instead of scrolling inside it. */}
            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable={mode === 'multi'}
              className="min-h-0 flex-1 overflow-y-auto"
            >
              {/* Screen-reader-only: the checkbox fill / top-of-list reorder a
                  pick causes is silent otherwise. Visual users already see it. */}
              {mode === 'multi' && (
                <span role="status" aria-live="polite" className="sr-only">
                  {t('filter.multi.selectedCount', { count: selected.length })}
                </span>
              )}
              {mode === 'single' && onClear && (
                <button
                  type="button"
                  role="option"
                  aria-selected={!hasSelection}
                  onClick={clearAxis}
                  className={`flex w-full items-center gap-2 px-3 ${ROW_TOUCH} text-left transition-colors hover:bg-bone ${
                    !hasSelection ? 'font-semibold text-brand' : 'text-ink'
                  }`}
                >
                  {t('filter.all')}
                </button>
              )}

              {nichesFirst && nicheSection}

              {catShown.map((c) => (
                <button
                  key={`cat-${c.slug}`}
                  type="button"
                  role="option"
                  aria-selected={isPicked('category', c.slug)}
                  onClick={() => pick({ kind: 'category', slug: c.slug, name: c.name })}
                  className={`flex w-full items-center gap-2 px-3 ${ROW_TOUCH} text-left transition-colors ${
                    isPicked('category', c.slug)
                      ? 'bg-brand-dim text-brand hover:bg-brand-dim'
                      : 'text-ink hover:bg-bone'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate text-left">{c.name}</span>
                  {/* Rendered even at ZERO — see the ИКПУ row below for why. */}
                  <span
                    className={`shrink-0 text-[11px] text-ink-muted ${c.count > 0 ? '' : 'opacity-60'}`}
                  >
                    {c.count}
                  </span>
                </button>
              ))}

              {/* Reveal the rest of the categories a page at a time. Keeping the
                  browse list short is what lifts the ИКПУ section below into view. */}
              {catMoreCount > 0 && (
                <button
                  type="button"
                  onClick={() => setCatLimit((n) => n + CAT_PAGE)}
                  className={`flex w-full items-center gap-1.5 px-3 ${MORE_ROW_TOUCH} text-left font-medium text-brand transition-colors hover:bg-bone`}
                >
                  <PickerArrow />
                  {t('filter.more.categories', { count: catMoreCount })}
                </button>
              )}

              {/* Precise ИКПУ niches — matches the exact product class.
                  BROWSE mode: collapsible sections (this file's own note on
                  `ikpuSections` above). SEARCH mode: the flat, ranked list
                  exactly as before. */}
              {browsingIkpu ? (
                <>
                  {ikpuSectionsShown.length > 0 && (
                    <div className="border-t border-edge bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                      {t('filter.ikpu.label')}
                    </div>
                  )}
                  {ikpuSectionsShown.map((section) => {
                    const expanded = expandedIkpuSections.has(section.root.code);
                    const hasChildren = section.children.length > 0;
                    // How many of this section's descendants are picked. Once
                    // collapsed, the section row is the ONLY thing on screen —
                    // without this a user who picked three codes inside it
                    // sees an untouched-looking row and has to reopen every
                    // section to find their own selection again.
                    const pickedInside = section.children.filter((c) =>
                      isPicked('ikpu', c.code),
                    ).length;
                    return (
                      /* A hairline between sections, and a faint ground under
                         an expanded one, so a section and its children read as
                         one block rather than as a run of same-looking rows.
                         `edge-light`, not `edge`: this separates peers, it does
                         not fence off a region. */
                      <div
                        key={`ikpu-section-${section.root.code}`}
                        className="border-t border-edge-light first:border-t-0"
                      >
                        {/* Two SEPARATE buttons side by side, not one row
                            with two handlers — a disclosure and a "select
                            this section" action need distinguishable tap
                            targets, including at 375px. Sticky only while
                            expanded: that is the state where its children
                            actually scroll underneath it. */}
                        <div
                          /* A sticky header must be OPAQUE — `brand-dim` is a
                             translucent rgba, so a picked section let its own
                             children scroll visibly through it. Picked+sticky
                             therefore uses the solid `brand-light`. */
                          className={`relative flex items-stretch ${
                            expanded ? 'sticky top-0 z-10 border-b border-edge' : ''
                          } ${
                            isPicked('ikpu', section.root.code)
                              ? expanded
                                ? 'bg-brand-light'
                                : 'bg-brand-dim'
                              : expanded
                                ? 'bg-bone-surface'
                                : ''
                          }`}
                        >
                          {pickedInside > 0 && !isPicked('ikpu', section.root.code) && (
                            <span
                              aria-label={t('filter.ikpu.pickedInside', { count: pickedInside })}
                              title={t('filter.ikpu.pickedInside', { count: pickedInside })}
                              className="absolute bottom-0 left-0 top-0 w-[3px] bg-brand"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            {ikpuRow(
                              section.root,
                              0,
                              undefined,
                              true,
                              hasChildren
                                ? { expanded, onToggle: () => toggleIkpuSection(section.root.code) }
                                : undefined,
                            )}
                          </div>
                          {hasChildren && (
                            <button
                              type="button"
                              onClick={() => toggleIkpuSection(section.root.code)}
                              aria-expanded={expanded}
                              aria-label={`${expanded ? t('filter.ikpu.collapse') : t('filter.ikpu.expand')}: ${section.root.name}`}
                              className="absolute bottom-1 right-3 flex h-6 w-6 items-center justify-end text-ink-soft transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:text-brand"
                            >
                              <PickerArrow rotated={expanded} />
                            </button>
                          )}
                        </div>
                        {/* The same 1rem gutter the root spends on its arrow,
                            left blank — so a child's code and title line up
                            with its section's, and every title in the list
                            shares one left edge. */}
                        {expanded && (
                          <div className="border-t border-edge-light">
                            {section.children.map((child) => ikpuRow(child))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {ikpuMoreSectionsCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setIkpuLimit((n) => n + IKPU_PAGE)}
                      className={`flex w-full items-center gap-1.5 px-3 ${MORE_ROW_TOUCH} text-left font-medium text-brand transition-colors hover:bg-bone`}
                    >
                      <PickerArrow />
                      {t('filter.more.ikpu')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {ikpuResults.length > 0 && (
                    <div className="border-t border-edge bg-bone px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
                      {t('filter.ikpu.label')}
                    </div>
                  )}
                  {ikpuResults.map((item) => ikpuRow(item))}

                  {/* Reveal more classifier codes a page at a time. */}
                  {ikpuMoreCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setIkpuLimit((n) => n + IKPU_PAGE)}
                      className={`flex w-full items-center gap-1.5 px-3 ${MORE_ROW_TOUCH} text-left font-medium text-brand transition-colors hover:bg-bone`}
                    >
                      <PickerArrow />
                      {t('filter.more.ikpu')}
                    </button>
                  )}
                </>
              )}

              {/* Nothing at all. In the catalogue the way-out line below IS the
                  answer; where a code is required, a hint to write one word
                  for what the person supplies — «Ничего не найдено» told them
                  nothing they could act on. While the request is still in
                  flight the box says «Ищем…», not «Ничего не найдено»: on a
                  phone, typing letter by letter flashed «nothing found» between
                  every two keystrokes (Redmi 15C, 2026-10-16). */}
              {catResults.length === 0 &&
                nicheResults.length === 0 &&
                ikpuResults.length === 0 &&
                !offerWordsSearch &&
                query.trim().length >= 2 && (
                  <div className="px-3 py-2 text-[12.5px] text-ink-muted">
                    {ikpuSettled ? t('filter.ikpu.noCodeHint') : t('ikpu.picker.searching')}
                  </div>
                )}
              {!nichesFirst && nicheSection}
            </div>
            {/* Outside the listbox: it is an action, not an option, and pinned
                under the scrolling list it stays in view. Same page — the
                words go into the catalogue's own search (`onSearchWords`). */}
            {offerWordsSearch && (
              <div className="shrink-0 border-t border-edge">
                <button
                  type="button"
                  onClick={() => {
                    onSearchWords?.(debounced);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="flex min-h-[44px] w-full items-center gap-1.5 px-3 py-2.5 text-left text-[14px] font-medium text-brand transition-colors hover:bg-bone md:min-h-0 md:py-2 md:text-[12.5px]"
                >
                  {t('filter.ikpu.searchWords', { q: debounced })}
                </button>
              </div>
            )}
          </div>,
          portalTarget,
        )}
    </div>
  );
}
